import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import useSWR from 'swr'
import * as UIcons from '@iconscout/react-unicons'
import BreadCrumb from 'components/BreadCrumb'
import FilterPill from '../../shared/FilterPill'
import RoleHeaderCard from './RoleHeaderCard'
import { PERMISSION_CATEGORIES, humanizePermission } from '../permissionCategories'

export default withTranslation()(function AssignedPermissionsView({ t, role, onBack, onEdit, onDuplicate, onToggleStatus }) {
  const { data: usersRes } = useSWR('user-mgmt/users')
  const { data: branchesRes } = useSWR('merchants/branches')

  const users = usersRes?.data?.data ?? []
  const branches = branchesRes?.data ?? []
  const branchNameById = branches.reduce((acc, item) => ({ ...acc, [item.id]: item.name }), {})
  const assignedUsers = users.filter((user) => user.roles?.some((r) => r.id === role.id))

  const [search, setSearch] = useState('')
  const [module, setModule] = useState('')

  const permissions = role.permissions ?? []

  const moduleOptions = [
    { label: t('settings.role_management.permissions_step.all_modules'), value: '' },
    ...PERMISSION_CATEGORIES.map((category) => ({ label: t(`settings.role_management.permissions_step.categories.${category.key}`), value: category.key }))
  ]

  const visibleCategories = PERMISSION_CATEGORIES
    .filter((category) => !module || category.key === module)
    .map((category) => ({
      ...category,
      permissions: category.permissions.filter((permission) => humanizePermission(permission).toLowerCase().includes(search.trim().toLowerCase()))
    }))
    .filter((category) => category.permissions.length)

  return (
    <section className="w-full max-w-4xl mx-auto">
      <div className="mb-5">
        <BreadCrumb
          items={[
            { name: t('settings.hub.title'), url: '/settings' },
            { name: t('settings.hub.team.title'), url: '/settings/team-access' },
            { name: t('settings.role_management.page_title'), url: '/settings/role-management' },
            { name: t('settings.role_management.detail.page_title'), onClick: onBack },
            { name: t('settings.role_management.detail.assigned_permissions') }
          ]}
        />
      </div>

      <RoleHeaderCard
        role={role}
        branchName={branchNameById[role.branch_id]}
        usersAssigned={role.users_count ?? assignedUsers.length}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onToggleStatus={onToggleStatus}
      />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('settings.role_management.permissions_step.search_placeholder')}
            className="w-full bg-white border border-border-lines rounded-full h-9 pl-10 pr-4 text-sm outline-none"
          />
          <UIcons.UilSearch className="text-placeholder absolute top-1/2 -translate-y-1/2 left-3.5" size={16} />
        </div>

        <FilterPill
          label={t('settings.role_management.permissions_step.all_modules')}
          active={module ? { label: moduleOptions.find((option) => option.value === module)?.label } : null}
          options={moduleOptions}
          onSelect={(option) => setModule(option.value)}
        />
      </div>

      {
        visibleCategories.map((category) => (
          <div key={category.key} className="w-full bg-white rounded-2xl border border-border-lines shadow-sm p-6 flex flex-col gap-4 mb-5">
            <p className="text-xl font-semibold text-secondary">{t(`settings.role_management.permissions_step.categories.${category.key}`)}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4">
              {
                category.permissions.map((permission) => {
                  const granted = permissions.includes(permission)
                  return (
                    <div key={permission} className="flex items-center gap-4 py-2">
                      {
                        granted ? (
                          <UIcons.UilCheckCircle size={24} className="text-success shrink-0" />
                        ) : (
                          <UIcons.UilCircle size={24} className="text-border-lines shrink-0" />
                        )
                      }
                      <p className={`text-base ${granted ? 'text-dark-blue' : 'text-placeholder'}`}>{humanizePermission(permission)}</p>
                    </div>
                  )
                })
              }
            </div>
          </div>
        ))
      }
    </section>
  )
})
